/**
 * Inferred risk-profile answers (TASK-619).
 *
 * Context + review panel for Section-2 answers derived from the KYC graph.
 * An inferred answer is NOT an answer for FinSA until the RM confirms it —
 * the engine enforces that server-side; this UI is where confirming happens.
 */

import React, { createContext, useContext } from 'react';
import { Sparkles, Check, Pencil } from 'lucide-react';
import type { InferredAnswerState } from '../../services/riskProfileService';

export interface InferredAnswersValue {
  /** `section2.<path>` → state; only server-known entries. */
  states: Record<string, InferredAnswerState>;
  /** Nested partial section2 with the inferred values. */
  values: Record<string, unknown>;
  notDerivable: Array<{ path: string; reason: string }>;
  confirmOne: (path: string) => Promise<void>;
  confirmAll: () => Promise<void>;
  overrideOne: (path: string) => Promise<void>;
}

export const InferredAnswersContext = createContext<InferredAnswersValue | null>(null);

export const useInferredAnswers = () => useContext(InferredAnswersContext);

/**
 * "section2.assetClassExperience.equities.knowledge" → "Equities knowledge"
 *
 * TASK-846: strips ANY `sectionN.` prefix, not just `section2.`. The ledger now also
 * carries `section3.totalNetWorthAttested` (the net worth the KYC file states), and with a
 * `section2`-only strip that rendered as "Section3.total net worth attested".
 */
export function labelForPath(path: string): string {
  const parts = path.replace(/^section\d+\./, '').split('.');
  const words = (s: string) =>
    s.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, (c) => c.toUpperCase());
  if (parts[0] === 'assetClassExperience') {
    return `${words(parts[1])} knowledge`;
  }
  return words(parts[0]);
}

/**
 * Value of `sectionN.a.b` inside the merged inferred-values object.
 *
 * TASK-846: section-agnostic for the same reason as `labelForPath` — the wizard merges the
 * `section2` and `section3` value objects the server returns, and a `section2`-only strip
 * would look up `section3.totalNetWorthAttested` under a key that does not exist.
 */
export function inferredValueAt(values: Record<string, unknown>, path: string): unknown {
  let node: unknown = values;
  for (const part of path.replace(/^section\d+\./, '').split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

/** Review panel rendered at the top of the Knowledge & Experience step. */
export const InferredAnswersPanel: React.FC = () => {
  const ctx = useInferredAnswers();
  if (!ctx) return null;
  const pending = Object.entries(ctx.states).filter(([, s]) => s.state === 'inferred');
  const confirmed = Object.entries(ctx.states).filter(([, s]) => s.state === 'confirmed');
  if (pending.length === 0 && confirmed.length === 0 && ctx.notDerivable.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6" data-testid="inferred-answers-panel">
      {pending.length > 0 && (
        <div className="border-2 border-dashed border-indigo-300 bg-indigo-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-semibold text-indigo-900">
              {pending.length} answer{pending.length !== 1 ? 's' : ''} inferred from the
              KYC profile — not counted for FinSA until you confirm
            </span>
            <button
              onClick={() => ctx.confirmAll()}
              className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
              Confirm all
            </button>
          </div>
          <div className="space-y-2">
            {pending.map(([path, st]) => (
              <div
                key={path}
                className="flex items-start justify-between gap-3 bg-white border border-indigo-100 rounded-md px-3 py-2"
              >
                <div className="min-w-0 text-sm">
                  <span className="font-medium text-gray-900">{labelForPath(path)}: </span>
                  <span className="text-gray-800">
                    {String(inferredValueAt(ctx.values, path) ?? '—')}
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">{st.provenance}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => ctx.confirmOne(path)}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100"
                  >
                    <Check className="w-3 h-3" /> Confirm
                  </button>
                  <button
                    onClick={() => ctx.overrideOne(path)}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100"
                    title="Answer this question yourself instead"
                  >
                    <Pencil className="w-3 h-3" /> Override
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmed.length > 0 && (
        <p className="text-xs text-gray-500">
          {confirmed.length} inferred answer{confirmed.length !== 1 ? 's' : ''} confirmed
          {confirmed[0][1].assessor ? ` by ${confirmed[0][1].assessor}` : ''}.
        </p>
      )}

      {ctx.notDerivable.length > 0 && (
        <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
          <span className="font-medium">Not derivable — ask at the next meeting: </span>
          {ctx.notDerivable.map((n) => labelForPath(n.path)).join(', ')}
        </div>
      )}
    </div>
  );
};
